import type * as atlas from 'azure-maps-control';
import { azureMapsKey } from './config';
import type { CreateMapOptions, MapController, MapMarker, MapProvider, MapView } from './MapProvider';

const DEFAULT_ZOOM = 10;

/**
 * The Azure Maps implementation of {@link MapProvider}.
 *
 * The SDK is imported dynamically inside {@link create} so its weight stays out of the initial bundle
 * and out of any code path that never shows a map — including the test renderer, which cannot run its
 * WebGL canvas. When no subscription key is configured the provider reports itself unconfigured and
 * {@link create} returns `null`, leaving the caller to show the map-unavailable state.
 */
export const azureMapsProvider: MapProvider = {
  get isConfigured(): boolean {
    return azureMapsKey() !== '';
  },

  async create(container: HTMLElement, options: CreateMapOptions): Promise<MapController | null> {
    const key = azureMapsKey();

    if (key === '') {
      return null;
    }

    const maps = await import('azure-maps-control');
    await import('azure-maps-control/dist/atlas.min.css');

    const map = new maps.Map(container, {
      center: [options.view.longitude, options.view.latitude],
      zoom: options.view.zoom ?? DEFAULT_ZOOM,
      style: 'road',
      authOptions: {
        authType: maps.AuthenticationType.subscriptionKey,
        subscriptionKey: key,
      },
    });

    const source = new maps.source.DataSource();
    const layer = new maps.layer.SymbolLayer(source, undefined, {
      iconOptions: { allowOverlap: true },
    });

    await ready(map);

    map.sources.add(source);
    map.layers.add(layer);

    if (options.onMarkerClick !== undefined) {
      map.events.add('click', layer, (event: atlas.MapMouseEvent) => {
        const shape = event.shapes?.[0];
        const id = shape !== undefined && 'getProperties' in shape
          ? (shape.getProperties() as { id?: string }).id
          : undefined;

        if (id !== undefined) {
          options.onMarkerClick?.(id);
        }
      });
    }

    if (options.onMapClick !== undefined) {
      map.events.add('click', (event: atlas.MapMouseEvent) => {
        // A click that hit a marker is handled by the layer listener above; ignore it here so
        // dropping a pin and selecting one do not fight.
        if ((event.shapes?.length ?? 0) > 0) {
          return;
        }

        const position = event.position;
        const longitude = position?.[0];
        const latitude = position?.[1];
        if (longitude !== undefined && latitude !== undefined) {
          options.onMapClick?.(latitude, longitude);
        }
      });
    }

    return {
      setMarkers(markers: readonly MapMarker[]): void {
        source.clear();
        source.add(
          markers.map((marker) =>
            new maps.data.Feature(
              new maps.data.Point([marker.longitude, marker.latitude]),
              { id: marker.id, selected: marker.selected === true },
            ),
          ),
        );
      },
      setView(view: MapView): void {
        map.setCamera({
          center: [view.longitude, view.latitude],
          zoom: view.zoom ?? map.getCamera().zoom,
        });
      },
      destroy(): void {
        map.dispose();
      },
    };
  },
};

/** Resolves once the map has fired its `ready` event, after which sources and layers can be added. */
function ready(map: atlas.Map): Promise<void> {
  return new Promise((resolve) => {
    map.events.add('ready', () => resolve());
  });
}
